import { UserRole } from "@joonapp/web-shared"
import { SessionBooking, SessionNote } from "@joonapp/web-shared"
import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "react-hot-toast"

import { updateSessionBooking, updateSessionNotes } from "./api"
import useCurrentClient from "../../pages/clients/hooks/useCurrentClient"
import { queryClient } from "../../queryClient"
import { QueryKey } from "../../types/queries"

export const useUpdateSessionNotesMutation = () => {
  return useMutation({
    mutationFn: (data: {
      noteId: number
      sessionBookingId: number
      note?: string
      visibilityRole?: UserRole.COACH | null
    }) => updateSessionNotes(data),

    // Optimistically update the UI before the server responds
    onMutate: (data) => {
      const previousData = queryClient.getQueryData<SessionBooking>([
        QueryKey.SESSION_BOOKING,
        data.sessionBookingId,
      ])

      if (!previousData) return

      const updatedSessionBooking = { ...previousData }

      // Prepare the updated note data
      const updatedNote = {
        noteId: data.noteId,
        ...(data.note && data.note.length > 0 && { note: data.note }),
        ...(data.visibilityRole !== undefined && {
          visibility_role: data.visibilityRole,
        }),
      }

      // Update the appropriate note based on the noteId
      switch (data.noteId) {
        case updatedSessionBooking.session_notes?.id:
          // Update main session notes
          const newNote = {
            ...updatedSessionBooking.session_notes,
            ...updatedNote,
          } as SessionNote
          updatedSessionBooking.session_notes = newNote
          break
        case updatedSessionBooking.session_summary_notes?.id:
          // Update summary notes
          const newSessionSummaryNote = {
            ...updatedSessionBooking.session_summary_notes,
            ...updatedNote,
          } as SessionNote
          updatedSessionBooking.session_summary_notes = newSessionSummaryNote
          break
        case updatedSessionBooking.topic_to_discuss_notes?.id:
          // Update topics to discuss
          const newTopicToDiscussNote = {
            ...updatedSessionBooking.topic_to_discuss_notes,
            ...updatedNote,
          } as SessionNote
          updatedSessionBooking.topic_to_discuss_notes = newTopicToDiscussNote
          break
      }

      // Update the cache with our optimistic changes
      queryClient.setQueryData(
        [QueryKey.SESSION_BOOKING, data.sessionBookingId],
        updatedSessionBooking
      )

      // Return previous data for rollback if needed
      return { previousData: updatedSessionBooking }
    },

    // After mutation completes (success or error), update cache with server data
    onSettled: (data) => {
      queryClient.invalidateQueries([QueryKey.COACH_TODOS])
      queryClient.setQueryData(
        [QueryKey.SESSION_BOOKING, data.sessionBookingId],
        data.previousData
      )
      queryClient.invalidateQueries([QueryKey.ALL_BILLING_PERIODS])
    },
  })
}

export const useUpdateSessionBookingMutation = () => {
  const { currentCoachingGroup } = useCurrentClient()
  return useMutation({
    mutationFn: updateSessionBooking,
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.SESSION_BOOKING, variables.sessionBookingId],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SESSION_BOOKINGS, currentCoachingGroup?.id],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SESSION_BILLING_PERIODS, currentCoachingGroup?.id],
      })
    },
    onError: (error) => {
      toast.error("Error updating session booking")
      Sentry.captureException(error)
    },
  })
}
